/**
 * Gestión de usuarios: quién es quién, qué roles tiene y en qué empresas trabaja.
 *
 * No inventa un endpoint propio: lee y escribe `base.user` por la API genérica
 * del ERP, igual que cualquier otra pantalla. El alta de una persona nueva se
 * hace en el formulario genérico del ERP —ahí ya están las validaciones y la
 * contraseña— y desde acá se enlaza; duplicar ese formulario sería duplicar
 * también sus reglas.
 *
 * La decisión de UX que importa: al lado de las casillas de roles hay un resumen
 * en vivo de lo que esos roles otorgan. Tildar «Contable» sin ver que incluye
 * eliminar asientos es exactamente el error que este panel existe para evitar.
 *
 * Los ids de grupo que la consola no reconoce (porque el servicio de roles no los
 * publicó) NO se pierden al guardar: el borrador arranca de la lista completa del
 * usuario y las casillas sólo agregan o quitan los conocidos.
 */

import { useEffect, useMemo, useState } from 'react';

import { Badge, Button } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import type { RecordData } from '@/lib/types';
import { useSessionStore } from '@/store/session';
import { BotonBloqueado } from '@/views/MotivoBloqueo';
import { estadoDeCuenta, GRUPO_ADMINISTRADOR } from '@/panel/acceso';
import { FormularioDeInvitacion, PanelDeAcceso, useEstadoDeAcceso } from '@/panel/AccesoUsuario';
import { RUTA_ROLES } from '@/panel/api';
import type { PanelUser } from '@/panel/api';
import type { Role } from '@/panel/contract';
import { describirError } from '@/panel/errores';
import { useEmpresas, useGuardarAsignacion, useMetaUsuario, useRoles, useUsuarios } from '@/panel/queries';
import { ResumenOtorga } from '@/panel/ResumenOtorga';
import {
  CampoBusqueda,
  Cargando,
  Casilla,
  Codigo,
  Fallo,
  FilaMaestra,
  Panel,
  Vacio,
  useNavegacionPorFlechas,
} from '@/panel/ui';
import { normalizarTexto } from '@/panel/vocabulario';

interface Borrador {
  grupos: number[];
  empresas: number[];
}

function mismosIds(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const ordenA = [...a].sort((x, y) => x - y);
  const ordenB = [...b].sort((x, y) => x - y);
  return ordenA.every((valor, indice) => valor === ordenB[indice]);
}

function alternar(lista: number[], id: number, incluir: boolean): number[] {
  if (incluir) return lista.includes(id) ? lista : [...lista, id];
  return lista.filter((valor) => valor !== id);
}

/** Texto secundario de la fila: los roles que ya tiene, o su ausencia. */
function resumenDeRoles(usuario: PanelUser, porId: Map<number, Role>): string {
  const nombres = usuario.nucleo_groups.map((id) => porId.get(id)?.name).filter((n): n is string => n !== undefined);
  const desconocidos = usuario.nucleo_groups.length - nombres.length;
  if (nombres.length === 0 && desconocidos === 0) return 'Sin roles asignados';
  const partes = [...nombres];
  if (desconocidos > 0) partes.push(`${desconocidos} sin identificar`);
  return partes.join(' · ');
}

export function UsuariosScreen() {
  const usuarios = useUsuarios();
  const meta = useMetaUsuario();
  const empresas = useEmpresas();
  const roles = useRoles();
  const guardar = useGuardarAsignacion();
  const sesion = useSessionStore((estado) => estado.user);

  const [filtro, setFiltro] = useState('');
  const [idElegido, setIdElegido] = useState<number | null>(null);
  const [borrador, setBorrador] = useState<Borrador | null>(null);
  const [rolEnFoco, setRolEnFoco] = useState<string | null>(null);
  /** Con el alta abierta, la columna derecha muestra el formulario y no la ficha. */
  const [invitando, setInvitando] = useState(false);

  // Se pide acá y no dentro de la ficha para que la lista pueda marcar de un
  // vistazo quién todavía no eligió su contraseña, sin una consulta por fila.
  const acceso = useEstadoDeAcceso();
  const puedeEntrarPorId = useMemo(() => {
    const indice = new Map<number, boolean>();
    for (const fila of acceso.data?.results ?? []) indice.set(fila.user_id, fila.puede_entrar);
    return indice;
  }, [acceso.data]);

  const lista = usuarios.data?.results ?? [];
  const puedeEscribir = meta.data?.permissions.write === true;

  const rolesPorId = useMemo(() => {
    const indice = new Map<number, Role>();
    for (const rol of roles.data ?? []) {
      if (rol.id !== null) indice.set(rol.id, rol);
    }
    return indice;
  }, [roles.data]);

  /**
   * Roles que se pueden asignar: los que el servicio publicó CON su id interno.
   * Se emparejan acá con su id para no arrastrar un `rol.id!` por toda la vista.
   */
  const asignables = useMemo(
    () => (roles.data ?? []).flatMap((rol) => (rol.id === null ? [] : [{ rol, id: rol.id }])),
    [roles.data],
  );

  const visibles = useMemo(() => {
    const termino = normalizarTexto(filtro).trim();
    if (termino === '') return lista;
    return lista.filter((usuario) =>
      normalizarTexto(`${usuario.name} ${usuario.email}`).includes(termino),
    );
  }, [lista, filtro]);

  const elegido = lista.find((usuario) => usuario.id === idElegido) ?? null;

  // Al cambiar de persona (o al llegar datos frescos del servidor) el borrador se
  // rehace desde el registro. Nunca se arrastra lo tildado para otro usuario.
  useEffect(() => {
    if (elegido === null) {
      setBorrador(null);
      return;
    }
    setBorrador({ grupos: [...elegido.nucleo_groups], empresas: [...elegido.companies] });
    setRolEnFoco(null);
  }, [elegido]);

  // La selección sigue al filtro: dejar abierta a una persona que la búsqueda
  // acaba de esconder deja la pantalla hablando de alguien que no está en la lista.
  useEffect(() => {
    if (idElegido !== null && visibles.some((usuario) => usuario.id === idElegido)) return;
    setIdElegido(visibles[0]?.id ?? null);
  }, [visibles, idElegido]);

  const moverFoco = useNavegacionPorFlechas();

  const sucio =
    elegido !== null &&
    borrador !== null &&
    (!mismosIds(borrador.grupos, elegido.nucleo_groups) || !mismosIds(borrador.empresas, elegido.companies));

  const rolesDelBorrador = (borrador?.grupos ?? [])
    .map((id) => rolesPorId.get(id))
    .filter((rol): rol is Role => rol !== undefined);

  const rolResaltado =
    rolEnFoco === null ? null : (asignables.find(({ rol }) => rol.code === rolEnFoco)?.rol ?? null);

  // ------------------------------------------------------------------------
  // Quitarse a uno mismo el rol que abre esta pantalla
  // ------------------------------------------------------------------------
  // Es el único cambio de esta consola que puede dejarla inaccesible, y estaba
  // saliendo con el mismo «Hay cambios sin guardar» que cambiar una empresa. El
  // servidor ya no deja quedarse sin ningún administrador (la guarda vive en
  // `base/security.py`), pero el caso «quedan otros pero el que se lo saca soy
  // yo» es legítimo y sigue mereciendo que se diga en voz alta antes.
  const idRolAdministrador =
    (roles.data ?? []).find((rol) => rol.code === GRUPO_ADMINISTRADOR)?.id ?? null;

  const pierdeAdministracion =
    elegido !== null &&
    borrador !== null &&
    idRolAdministrador !== null &&
    elegido.nucleo_groups.includes(idRolAdministrador) &&
    !borrador.grupos.includes(idRolAdministrador);

  const esMiPropioUsuario = sesion !== null && elegido !== null && elegido.id === sesion.id;

  /** Cuántas personas activas seguirían pudiendo administrar si se guarda esto. */
  const administradoresQueQuedan =
    idRolAdministrador === null
      ? null
      : lista.filter(
          (usuario) =>
            usuario.is_active &&
            usuario.id !== elegido?.id &&
            (usuario.is_superuser || usuario.nucleo_groups.includes(idRolAdministrador)),
        ).length;

  const necesitaConfirmar = pierdeAdministracion;
  const [confirmado, setConfirmado] = useState(false);

  // La confirmación no se arrastra: cambiar de persona o deshacer el cambio la
  // vuelve a pedir.
  useEffect(() => {
    if (!necesitaConfirmar) setConfirmado(false);
  }, [necesitaConfirmar, idElegido]);

  function guardarCambios() {
    if (elegido === null || borrador === null) return;
    const cambios: { nucleo_groups?: number[]; companies?: number[] } = {};
    if (!mismosIds(borrador.grupos, elegido.nucleo_groups)) cambios.nucleo_groups = borrador.grupos;
    if (!mismosIds(borrador.empresas, elegido.companies)) cambios.companies = borrador.empresas;

    guardar.mutate(
      { id: elegido.id, cambios },
      {
        onSuccess: () => {
          notify.success('Cambios guardados', `Se actualizó el acceso de ${elegido.name || elegido.email}.`);
        },
        onError: (error: unknown) => {
          notify.error(
            'No se pudo guardar',
            error instanceof Error ? error.message : 'El servidor rechazó el cambio.',
          );
        },
      },
    );
  }

  if (usuarios.isPending) {
    return (
      <Panel title="Usuarios">
        <Cargando label="Consultando el padrón de usuarios…" />
      </Panel>
    );
  }

  if (usuarios.error !== null) {
    const fallo = describirError(usuarios.error, '/api/v1/model/base.user/');
    return (
      <Fallo
        title={fallo.title}
        message={fallo.message}
        detail={fallo.detail}
        onRetry={() => {
          void usuarios.refetch();
        }}
      />
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[22rem_minmax(0,1fr)] xl:grid-cols-[24rem_minmax(0,1fr)]">
      {/* Columna izquierda: padrón */}
      <div className="lg:sticky lg:top-[6.5rem] lg:self-start">
        <Panel bodyClassName="p-3" className="mb-3">
          <CampoBusqueda
            id="buscar-usuario"
            label="Buscar persona"
            placeholder="nombre o email"
            value={filtro}
            onChange={setFiltro}
          />
          <p className="mt-2 text-xs text-fg-muted">
            {visibles.length} de {lista.length} {lista.length === 1 ? 'persona' : 'personas'}
          </p>
        </Panel>

        <Panel
          title="Personas"
          bodyClassName="p-0"
          actions={
            // Antes esto mandaba al formulario genérico del ERP, que creaba la
            // ficha y dejaba a la persona SIN poder entrar (la contraseña queda
            // inutilizable). Ahora el alta y la entrega del enlace son un solo
            // gesto, que es como el administrador lo piensa.
            // Esta MISMA pantalla ya resolvía bien el caso idéntico 350 líneas
            // más abajo («Tu usuario puede mirar esta pantalla pero no modificar
            // usuarios.», en un `aria-live` al lado del botón de guardar) y acá
            // dejaba el botón gris y mudo. Dos criterios en el mismo archivo:
            // ahora es uno.
            puedeEscribir ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setInvitando(true);
                }}
              >
                Invitar persona
              </Button>
            ) : (
              <BotonBloqueado
                variant="primary"
                size="sm"
                label="Invitar persona"
                motivo="Tu usuario puede mirar esta pantalla pero no modificar usuarios, así que no puede invitar a nadie. Pedíselo a quien administra los accesos."
              />
            )
          }
        >
          {visibles.length === 0 ? (
            <div className="p-3">
              <Vacio title="Sin resultados" description="Probá con otro nombre o email." />
            </div>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto" onKeyDown={moverFoco}>
              {visibles.map((usuario) => (
                <FilaMaestra
                  key={usuario.id}
                  selected={usuario.id === idElegido}
                  onSelect={() => {
                    setIdElegido(usuario.id);
                  }}
                >
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-medium">{usuario.name || usuario.email}</span>
                    {/* Mismo criterio y mismas palabras que la ficha de la
                        derecha: ver `estadoDeCuenta`. En la lista se calla el
                        caso normal —«Activa»— porque marcar a las veinte
                        personas con un chip verde no informa nada; lo que hay
                        que ver de un vistazo es quién NO puede entrar. */}
                    {(() => {
                      const estado = estadoDeCuenta(
                        usuario.is_active,
                        puedeEntrarPorId.get(usuario.id),
                      );
                      if (estado === null || estado.clave === 'activa') return null;
                      return (
                        <Badge tone={estado.tono} title={estado.detalle}>
                          {estado.etiqueta}
                        </Badge>
                      );
                    })()}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-fg-faint">{usuario.email}</span>
                  <span className="mt-0.5 block truncate text-xs text-fg-muted">
                    {resumenDeRoles(usuario, rolesPorId)}
                  </span>
                </FilaMaestra>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Columna derecha: ficha y asignación. `min-w-0` por lo mismo que en
          Permisos: sin eso, un email largo estira la grilla en movil. */}
      <div className="min-w-0 space-y-4">
        {invitando ? (
          <FormularioDeInvitacion
            roles={asignables}
            empresas={empresas.data?.results ?? []}
            onCerrar={() => {
              setInvitando(false);
            }}
          />
        ) : elegido === null || borrador === null ? (
          <Panel>
            <Vacio title="Elegí una persona de la lista" />
          </Panel>
        ) : (
          <>
            <Panel>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold tracking-tight">{elegido.name || elegido.email}</h2>
                  <p className="mt-1 text-sm text-fg-muted">{elegido.email}</p>
                  <p className="mt-2 flex flex-wrap gap-1.5">
                    {/* El chip decía «Activo» mirando sólo `is_active`, así que
                        una persona recién invitada —que todavía no tiene
                        contraseña y no puede entrar— salía en verde acá y «Sin
                        activar» en la lista de al lado, al mismo tiempo. Ahora
                        las dos preguntan lo mismo. Ver `estadoDeCuenta`. */}
                    {(() => {
                      const estado = estadoDeCuenta(
                        elegido.is_active,
                        puedeEntrarPorId.get(elegido.id),
                      );
                      if (estado === null) return null;
                      return (
                        <Badge tone={estado.tono} title={estado.detalle}>
                          {estado.etiqueta}
                        </Badge>
                      );
                    })()}
                    {elegido.is_superuser && <Badge tone="danger">Superusuario</Badge>}
                    {elegido.is_staff && <Badge tone="neutral">Panel técnico</Badge>}
                    <Badge tone="neutral">
                      {elegido.companies.length} {elegido.companies.length === 1 ? 'empresa' : 'empresas'}
                    </Badge>
                  </p>
                  {elegido.is_superuser && (
                    <p className="mt-3 max-w-xl rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-xs">
                      Esta persona es superusuaria: <strong>saltea el control de permisos por completo</strong>.
                      Los roles de abajo no la limitan. Quitarle esa condición se hace desde el panel técnico,
                      no desde acá.
                    </p>
                  )}
                </div>
                <a
                  href={`/m/base.user/${elegido.id}`}
                  className="rounded-md border border-line px-3 py-1.5 text-xs font-medium hover:bg-surface-raised"
                >
                  Abrir ficha completa
                </a>
              </div>
            </Panel>

            {/* ------------------------------------------------------------ */}
            {/* Acceso: si puede entrar, y cómo darle la llave                 */}
            {/* ------------------------------------------------------------ */}
            {/* Va ARRIBA de los roles a propósito: de nada sirve afinarle los
                permisos a alguien que todavía no puede entrar. */}
            <PanelDeAcceso usuario={elegido} />

            {/* ------------------------------------------------------------ */}
            {/* Roles + lo que otorgan, uno al lado del otro                  */}
            {/* ------------------------------------------------------------ */}
            <Panel
              title="Roles"
              subtitle="Los permisos se suman: alcanza con que un rol lo permita"
              bodyClassName="p-0"
            >
              <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_20rem]">
                <div className="p-4">
                  {roles.isPending ? (
                    <Cargando label="Consultando los roles…" />
                  ) : roles.error !== null ? (
                    <Fallo
                      title={describirError(roles.error, RUTA_ROLES).title}
                      message={
                        'Sin la lista de roles no se puede asignar a ciegas, así que la consola no lo ofrece. ' +
                        'Las empresas de abajo sí se pueden cambiar.'
                      }
                      detail={describirError(roles.error, RUTA_ROLES).detail}
                      onRetry={() => {
                        void roles.refetch();
                      }}
                    />
                  ) : asignables.length === 0 ? (
                    <Vacio
                      title="No hay roles asignables"
                      description="El servicio devolvió roles pero sin su identificador interno, y sin él la asignación tocaría el rol equivocado."
                    />
                  ) : (
                    <ul className="space-y-2">
                      {asignables.map(({ rol, id }) => {
                        const marcado = borrador.grupos.includes(id);
                        return (
                          <li
                            key={rol.code}
                            onMouseEnter={() => {
                              setRolEnFoco(rol.code);
                            }}
                            onFocus={() => {
                              setRolEnFoco(rol.code);
                            }}
                          >
                            <Casilla
                              checked={marcado}
                              disabled={!puedeEscribir}
                              onChange={(valor) => {
                                setRolEnFoco(rol.code);
                                setBorrador({ ...borrador, grupos: alternar(borrador.grupos, id, valor) });
                              }}
                              title={
                                <span className="flex flex-wrap items-baseline gap-2">
                                  {rol.name}
                                  <Codigo>{rol.code}</Codigo>
                                </span>
                              }
                              description={
                                rol.description !== ''
                                  ? rol.description
                                  : rol.userCount === null
                                    ? undefined
                                    : `Lo tienen ${rol.userCount} ${rol.userCount === 1 ? 'persona' : 'personas'}`
                              }
                            />
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {(() => {
                    const desconocidos = borrador.grupos.filter((id) => !rolesPorId.has(id));
                    if (desconocidos.length === 0) return null;
                    return (
                      <p className="mt-3 rounded-md border border-line bg-app-subtle px-3 py-2 text-xs text-fg-muted">
                        Esta persona tiene {desconocidos.length} grupo(s) que la consola no puede describir
                        porque el servicio de roles no los publicó. Se conservan tal cual al guardar.
                      </p>
                    );
                  })()}
                </div>

                {/* El aside: qué otorga lo que estoy por tildar. */}
                <aside className="border-t border-line bg-app-subtle p-4 lg:border-l lg:border-t-0">
                  {/* Para un superusuario este resumen no describe lo que puede
                      hacer: describe lo que le aportan los roles, que es otra
                      cosa y menos. Sin este cartel, «Con estos roles va a poder:
                      3 áreas» se lee como el techo de esa persona cuando en
                      realidad abre el sistema entero. */}
                  {elegido.is_superuser && (
                    <p className="mb-3 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-xs">
                      Ojo: además de esto, por ser superusuaria <strong>puede todo</strong> en todas las
                      áreas. Lo de abajo es sólo lo que le aportan los roles.
                    </p>
                  )}
                  {rolResaltado !== null ? (
                    <ResumenOtorga
                      roles={[rolResaltado]}
                      titulo={`Qué otorga «${rolResaltado.name}»`}
                      vacio="Este rol no concede operaciones por sí solo."
                    />
                  ) : (
                    <ResumenOtorga
                      roles={rolesDelBorrador}
                      titulo="Con estos roles va a poder"
                      vacio={
                        // Tener cero roles y no poder describirlos son cosas
                        // distintas, y confundirlas acá seria afirmar que una
                        // persona no puede hacer nada cuando quiza pueda todo.
                        borrador.grupos.length > 0
                          ? 'La consola no puede describir sus roles porque el servicio no respondió.'
                          : elegido.is_superuser
                            ? // Decir «sin roles no ve nada» de un superusuario
                              // es afirmar lo contrario de lo que pasa.
                              'No tiene roles asignados, pero por ser superusuaria abre igual todo el sistema.'
                            : 'Sin roles no ve nada del sistema, ni siquiera las listas.'
                      }
                    />
                  )}
                  {asignables.length > 0 && (
                    <p className="mt-4 text-2xs text-fg-faint">
                      Pasá el mouse o tabulá por un rol para ver sólo lo que aporta ese.
                    </p>
                  )}
                </aside>
              </div>
            </Panel>

            {/* ------------------------------------------------------------ */}
            {/* Empresas                                                      */}
            {/* ------------------------------------------------------------ */}
            <Panel title="Empresas habilitadas" subtitle="Los datos que puede ver, empresa por empresa">
              {empresas.isPending ? (
                <Cargando label="Consultando empresas…" />
              ) : empresas.error !== null ? (
                <Fallo
                  title="No se pudieron leer las empresas"
                  message={describirError(empresas.error, '/api/v1/model/base.company/').message}
                  onRetry={() => {
                    void empresas.refetch();
                  }}
                />
              ) : (empresas.data?.results.length ?? 0) === 0 ? (
                <Vacio title="No hay empresas cargadas" />
              ) : (
                <ul className="grid gap-2 sm:grid-cols-2">
                  {(empresas.data?.results ?? []).map((empresa: RecordData) => (
                    <li key={empresa.id}>
                      <Casilla
                        checked={borrador.empresas.includes(empresa.id)}
                        disabled={!puedeEscribir}
                        onChange={(valor) => {
                          setBorrador({ ...borrador, empresas: alternar(borrador.empresas, empresa.id, valor) });
                        }}
                        title={empresa.display_name}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            {/* ------------------------------------------------------------ */}
            {/* Barra de guardado                                             */}
            {/* ------------------------------------------------------------ */}
            <div
              className="sticky bottom-0 space-y-3 rounded-md border border-line bg-surface px-4 py-3 shadow-lg"
              role="region"
              aria-label="Guardar cambios"
            >
              {/* El aviso que faltaba: quitarse el rol que abre esta consola no
                  puede salir con el mismo cartel que cambiar una empresa. */}
              {necesitaConfirmar && (
                <div className="rounded-md border border-danger/40 bg-danger-soft px-3 py-2.5 text-xs">
                  <p className="font-semibold">
                    {esMiPropioUsuario
                      ? 'Estás por quitarte a vos misma el rol «Administrador».'
                      : `Estás por quitarle el rol «Administrador» a ${elegido.name || elegido.email}.`}
                  </p>
                  <p className="mt-1">
                    {esMiPropioUsuario
                      ? 'Al guardar perdés el acceso a esta consola: no vas a poder volver a entrar a Permisos ni a Usuarios, ni devolverte el rol desde la interfaz.'
                      : 'Esa persona pierde el acceso a esta consola y a la administración de la organización.'}
                    {administradoresQueQuedan === null
                      ? ''
                      : administradoresQueQuedan === 0
                        ? ' No queda ninguna otra persona activa que pueda administrar, así que el servidor va a rechazar el cambio.'
                        : administradoresQueQuedan === 1
                          ? ' Queda 1 persona activa que puede administrar y devolverlo.'
                          : ` Quedan ${administradoresQueQuedan} personas activas que pueden administrar y devolverlo.`}
                  </p>
                  <label className="mt-2 flex cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      checked={confirmado}
                      onChange={(evento) => {
                        setConfirmado(evento.target.checked);
                      }}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--danger))]"
                    />
                    <span>Entiendo lo que pasa al guardar y quiero hacerlo igual.</span>
                  </label>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-fg-muted" aria-live="polite">
                  {meta.error !== null
                    ? 'No se pudo confirmar si tenés permiso para modificar usuarios: el servidor decide al guardar.'
                    : !puedeEscribir
                      ? 'Tu usuario puede mirar esta pantalla pero no modificar usuarios.'
                      : necesitaConfirmar && !confirmado
                        ? 'Hay cambios sin guardar. Confirmá el aviso de arriba para poder guardarlos.'
                        : sucio
                          ? 'Hay cambios sin guardar.'
                          : 'Todo guardado.'}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!sucio || guardar.isPending}
                    onClick={() => {
                      setBorrador({ grupos: [...elegido.nucleo_groups], empresas: [...elegido.companies] });
                    }}
                  >
                    Descartar
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={guardar.isPending}
                    disabled={!sucio || !puedeEscribir || (necesitaConfirmar && !confirmado)}
                    onClick={guardarCambios}
                  >
                    Guardar cambios
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
